import { useState, useMemo, useEffect, useRef }  from 'react';
import { useNavigate }       from 'react-router-dom';
import { useCartStore, pendingKitchenItems, courseName, seatsLabel, nombreDe, lineIdFor } from '../../store/cartStore.js';
import { agruparParaCaptura } from '../../lib/comensales.js';
import ComensalSheet from '../ComensalSheet.jsx';
import SeatsSheet from '../SeatsSheet.jsx';
import ApuntarASheet from '../ApuntarASheet.jsx';
import MenuSheet from '../MenuSheet.jsx';
import { useConfirmacion } from '../ConfirmSheet.jsx';
import { useEsTelefono } from '../../lib/viewport.js';
import { usarPermisos } from '../../lib/permisos.js';
import AssignTableSheet from '../AssignTableSheet.jsx';
import { useOutboxStore } from '../../store/outboxStore.js';
import { waiterId } from '../../store/waiterStore.js';
import { isOfflineError } from '../../api/client.js';
import { useTerminalStore }  from '../../store/terminalStore.js';
import { useTabsStore }       from '../../store/tabsStore.js';
import { useSessionStore }    from '../../store/sessionStore.js';
import { useAuthStore }       from '../../store/authStore.js';
import { usePrinterStore }    from '../../store/printerStore.js';
import { usarPlan } from '../../lib/plan.js';
import { fireKitchenOrder }   from '../../api/kitchen.api.js';
import { thermalPrinter }     from '../../native/thermalPrinter.js';
import { v4 as uuidv4 }       from 'uuid';
import CustomerPickerModal    from '../CustomerPickerModal.jsx';
import ItemEditSheet          from '../ItemEditSheet.jsx';
import CashMovementModal      from '../CashMovementModal.jsx';
import { IcoClose, IcoCash, IcoReceipt, IcoTrash, IcoTabs, IcoUser, IcoEdit, IcoArrowUp, IcoWarning, IcoCheck } from '../common/Icons.jsx';
import styles from './CartPanel.module.css';

const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
// Precio unitario en la línea: sin centavos cuando no los hay ("28 × $285").
// Con ".00" la fila de etiquetas se partía en dos en un panel de 326 px.
const fmtCorto = (n) => Number.isInteger(Number(n)) ? `$${Number(n).toLocaleString('es-MX')}` : fmt(n);

export default function CartPanel() {
  const navigate      = useNavigate();
  const permisos = usarPermisos();
  const activeSeat    = useCartStore((s) => s.activeSeat);
  const setActiveSeat = useCartStore((s) => s.setActiveSeat);
  const setItemSeats  = useCartStore((s) => s.setItemSeats);
  const comensales    = useCartStore((s) => s.comensales);
  const addComensal   = useCartStore((s) => s.addComensal);
  const renameComensal = useCartStore((s) => s.renameComensal);
  const removeComensal = useCartStore((s) => s.removeComensal);
  const [seatsPara, setSeatsPara] = useState(null);   // línea que abrió la hoja de compartir
  const [comensalPara, setComensalPara] = useState(null);  // comensal que se está editando
  // Lo último que se movió de comensal, para poder deshacerlo. Cambiar de
  // comensal mueve dinero de una persona a otra: sin deshacer, un toque de más
  // en un chip obliga a reconstruir la cuenta a mano y el mesero deja de
  // tocarlo. Con deshacer, equivocarse cuesta un toque.
  const [deshacer, setDeshacer] = useState(null);   // { lineId, seats, texto }
  // En teléfono el carrito es una hoja inferior: arranca colapsada para que el
  // catálogo tenga la pantalla completa, que es donde el mesero pasa el tiempo.
  const [hojaAbierta, setHojaAbierta] = useState(false);
  const [asignarOpen, setAsignarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);          // "⋯" de la cuenta
  const [apuntarOpen, setApuntarOpen] = useState(false);    // "¿A quién le apunto?" (teléfono)
  const telefono = useEsTelefono();
  const { pedir, Confirmacion } = useConfirmacion();
  const items         = useCartStore((s) => s.items);
  const customer      = useCartStore((s) => s.customer);
  const discount      = useCartStore((s) => s.discount);
  const subtotal      = useCartStore((s) => s.getSubtotal());
  const total         = useCartStore((s) => s.getTotal());
  const updateQty     = useCartStore((s) => s.updateQuantity);
  const removeItem    = useCartStore((s) => s.removeItem);
  const clearCart     = useCartStore((s) => s.clearCart);
  const setDiscount   = useCartStore((s) => s.setDiscount);
  const note          = useCartStore((s) => s.note);
  const setNote       = useCartStore((s) => s.setNote);

  const terminalStatus = useTerminalStore((s) => s.status);
  const terminalReader = useTerminalStore((s) => s.reader);

  const tabs          = useTabsStore((s) => s.tabs);
  const activeTabId   = useTabsStore((s) => s.activeTabId);
  const createTab     = useTabsStore((s) => s.createTab);
  const saveActive    = useTabsStore((s) => s.saveActive);
  const saving        = useTabsStore((s) => s.saving);
  const tabsMsg       = useTabsStore((s) => s.lastMsg);
  const clearTabsMsg  = useTabsStore((s) => s.clearMsg);
  const clearActiveTab = useTabsStore((s) => s.clearActiveTab);
  const activeTab     = tabs.find((t) => t.id === activeTabId) ?? null;

  const [pickerOpen,   setPickerOpen]   = useState(false);
  const [discOpen,     setDiscOpen]     = useState(false);
  const [discType,     setDiscType]     = useState('pct');
  const [discValue,    setDiscValue]    = useState('');
  const [editItem,     setEditItem]     = useState(null);
  const [noteOpen,     setNoteOpen]     = useState(false);
  const [saveTabOpen,    setSaveTabOpen]    = useState(false);
  const [tabName,        setTabName]        = useState('');
  const [movementOpen,   setMovementOpen]   = useState(false);

  // ── Comandas ──────────────────────────────────────────────────────────────
  const session        = useSessionStore((s) => s.session);
  const terminal       = useSessionStore((s) => s.terminal);
  const user           = useAuthStore((s) => s.user);
  const markFired      = useCartStore((s) => s.markFired);
  // Comensales y mesas son del servicio en mesa: el giro (¿le aplica?) y el
  // plan (¿lo paga?). Marchar y los tiempos, además, de que el negocio use
  // cocina: con una sola tablet que toma la orden y cobra, no hay a quién
  // mandarle nada.
  const plan          = usarPlan();
  const esRestaurante = plan.servicioEnMesa;
  const usaCocina     = plan.cocina;

  // Quiénes están en la mesa. Sale del estado de la cuenta, no de los
  // platillos: un comensal al que todavía no se le apunta nada sigue estando
  // sentado y sigue apareciendo.
  const comensalesEnMesa = useMemo(
    () => [...new Set([...comensales.map((c) => c.n), ...items.flatMap((i) => i.seats ?? [])])].sort((a, b) => a - b),
    [items, comensales]
  );

  // La cuenta en bloques por comensal, como sale en el ticket. `null` cuando
  // nadie separó la mesa: ahí el carrito es la lista de siempre.
  const bloques = useMemo(
    () => (esRestaurante ? agruparParaCaptura(items, comensales) : null),
    [items, comensales, esRestaurante]
  );

  /**
   * Mueve una línea de comensal dejando cómo deshacerlo.
   *
   * Todo cambio de dueño pasa por aquí —el chip, la hoja de compartir— para que
   * no haya ningún camino que mueva dinero sin salida de emergencia.
   *
   * Dos cuidados que no son evidentes: al moverla, la línea CAMBIA de identidad
   * —los comensales entran en el `lineId`—, así que deshacer tiene que apuntar
   * al identificador nuevo y no al que se tocó. Y si en el destino ya había ese
   * mismo platillo, las dos líneas se funden en una: ahí no se ofrece deshacer,
   * porque devolverla se llevaría también la del otro comensal, y un deshacer
   * que mueve de más es peor que no tenerlo.
   */
  const moverA = (item, seats, texto) => {
    const destino = lineIdFor(item.product, item.modifiers ?? [], item.course ?? 1, seats, item.note);
    const seFunde = items.some((i) => i.lineId === destino && i.lineId !== item.lineId);
    setItemSeats(item.lineId, seats);
    setDeshacer(seFunde ? null : { lineId: destino, seats: item.seats ?? [], texto });
  };

  useEffect(() => {
    if (!deshacer) return undefined;
    const t = setTimeout(() => setDeshacer(null), 5000);
    return () => clearTimeout(t);
  }, [deshacer]);

  // ¿Esta cuenta tiene algo esperando en la cola durable? Es lo único que
  // convierte el renglón de estado en un botón: sin red, el mesero necesita
  // poder reintentar.
  const pendienteDeSubir = useOutboxStore((s) => !!s.pendientes[activeTabId]);

  // Sin servicio en mesa no hay cocina a la que marchar (el servidor rechaza
  // la comanda), y sin cocina tampoco: ni se ofrece.
  const pendingKitchen = useMemo(
    () => (usaCocina ? pendingKitchenItems(items) : []),
    [items, usaCocina]
  );
  const setCourse      = useCartStore((s) => s.setCourse);

  // Se marcha UN tiempo a la vez, el más bajo que tenga pendientes. Mandar
  // entradas y postres juntos a la cocina es exactamente lo que esta función
  // existe para evitar.
  const tiempoAMarchar = useMemo(() => {
    const tiempos = pendingKitchen.map(({ item }) => item.course ?? 1);
    return tiempos.length ? Math.min(...tiempos) : null;
  }, [pendingKitchen]);

  const aMarchar = useMemo(
    () => pendingKitchen.filter(({ item }) => (item.course ?? 1) === tiempoAMarchar),
    [pendingKitchen, tiempoAMarchar]
  );
  const printerDevId   = usePrinterStore((s) => s.deviceId);
  const printerTrans   = usePrinterStore((s) => s.transport);
  const [firing,       setFiring]       = useState(false);
  const [kitchenMsg,   setKitchenMsg]   = useState(null);

  const pendingCount = aMarchar.reduce((s, x) => s + x.pending, 0);
  const totalPendiente = pendingKitchen.reduce((s, x) => s + x.pending, 0);

  // Auto-guardado de la cuenta activa: la cuenta es compartida, así que lo
  // que este mesero agrega debe verse en las demás tablets sin que tenga que
  // acordarse de "Guardar". Debounce corto para no subir cada toque.
  const autosaveRef = useRef(null);
  useEffect(() => {
    if (!activeTabId) return;
    clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => {
      saveActive().catch((err) => setKitchenMsg(err.response?.data?.error ?? 'No se pudo guardar la cuenta en la caja'));
    }, 900);
    return () => clearTimeout(autosaveRef.current);
    // `comensales` entra en las dependencias por la misma razón que `items`:
    // sentar a alguien, ponerle nombre o quitarlo es un cambio de la cuenta.
    // Sin esto, dar de alta un comensal no llegaba al servidor y al recargar
    // —o desde la otra tablet— la mesa volvía a no tener a nadie.
  }, [items, customer, comensales, discount, note, activeTabId, saveActive]);

  // Avisos que vienen de otras tablets (cobraron o cerraron la cuenta que traíamos)
  useEffect(() => {
    if (!tabsMsg) return;
    setKitchenMsg(tabsMsg);
    const t = setTimeout(() => { clearTabsMsg(); setKitchenMsg(null); }, 5000);
    return () => clearTimeout(t);
  }, [tabsMsg, clearTabsMsg]);

  const handleFireKitchen = async () => {
    if (!aMarchar.length || firing) return;
    setFiring(true);
    setKitchenMsg(null);

    let encoladas = 0;

    // Una comanda por estación: la barra no debe recibir la orden de la cocina
    const porEstacion = new Map();
    for (const { item, pending } of aMarchar) {
      const est = item.product.station || 'Cocina';
      if (!porEstacion.has(est)) porEstacion.set(est, []);
      porEstacion.get(est).push({ item, pending });
    }

    try {
      for (const [station, lineas] of porEstacion) {
        const client_uuid = uuidv4();
        // La comandera no manda sesión aunque tenga una guardada: si este
        // aparato fue caja antes, ese turno ya no es suyo y el id viejo solo
        // confunde. El servidor resuelve a qué turno del negocio pertenece.
        const payload = {
          client_uuid,
          // Quién manda la comanda. En tablet compartida es el del PIN: la
          // cocina necesita saber a quién preguntarle, y "Caja 1" no sirve.
          waiter_id:   waiterId(),
          session_id:  permisos.comandera ? null : (session?._id ?? null),
          terminal_id: terminal?._id ?? null,
          tab_id:      activeTabId ?? null,
          tab_name:    activeTab?.name ?? null,
          station,
          course:      tiempoAMarchar,
          course_name: courseName(tiempoAMarchar),
          note:        note || null,
          items: lineas.map(({ item, pending }) => ({
            product_id: item.product._id,
            name:       item.product.name,
            quantity:   pending,
            // A quién va el platillo. La cocina lo necesita para emplatar y el
            // mesero para servir sin preguntar "¿de quién era el pescado?".
            seats:      item.seats ?? [],
            // La nota de la línea, para que el cocinero la lea junto al
            // platillo y no en el pie de la comanda.
            note:       item.note || null,
            modifiers:  (item.modifiers ?? []).map((m) => ({
              group_name: m.group_name, option_name: m.option_name,
            })),
          })),
        };

        let created, data;
        try {
          ({ created, data } = await fireKitchenOrder(payload));
        } catch (err) {
          // Se encola tanto si no hubo red como si el negocio todavía no puede
          // recibirla —`retriable`, o sea "no hay caja abierta"—. Las dos son
          // "aún no", no "está mal": la cola las entrega cuando se pueda. Solo
          // un rechazo de verdad se le muestra al mesero.
          const esperable = isOfflineError(err) || err.response?.data?.retriable === true;
          if (!esperable) throw err;
          useOutboxStore.getState().encolarComanda(payload);
          encoladas++;
          continue;
        }

        // Solo se imprime lo que el servidor aceptó como nuevo. Si `created`
        // es false fue un reintento y la cocina ya tiene el papel. La
        // comandera no imprime: el teléfono del mesero no trae impresora y
        // avisar "no se pudo imprimir" en cada comanda solo asusta.
        if (created && !permisos.comandera) {
          if (printerDevId && !thermalPrinter.isConnected()) {
            await thermalPrinter.autoReconnect(printerDevId, printerTrans).catch(() => {});
          }
          try {
            await thermalPrinter.printComanda({
              folio:    data.folio,
              station,
              tabName:  activeTab?.name ?? null,
              cashier:  user?.name ?? user?.email ?? null,
              date:     new Date().toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }),
              items:    lineas.map(({ item, pending }) => ({
                name:      item.product.name,
                quantity:  pending,
                modifiers: item.modifiers ?? [],
                note:      item.note || null,
                // Para agrupar el papel por comensal, como la pantalla.
                seats:     item.seats ?? [],
              })),
              // Los apodos, para que el bloque diga "SRA" y no "COMENSAL 2":
              // el cocinero emplata leyendo esto.
              comensales,
              note: note || null,
            });
          } catch (err) {
            // La comanda ya está en el servidor: la pantalla de cocina la ve
            // aunque el papel no haya salido. Se avisa, no se aborta.
            setKitchenMsg(`Comanda #${data.folio} enviada, pero no se pudo imprimir: ${err.message}`);
          }
        }
      }

      // Marcar enviado solo después de que el servidor confirmó todo, y subir
      // la cuenta de inmediato: otra tablet que la abra no debe re-enviar.
      // Solo el tiempo que se marchó: marcar todos los pendientes daría por
      // enviados el fuerte y el postre, que siguen esperando en la mesa.
      markFired(aMarchar.map(({ item }) => item.lineId));
      if (activeTabId) saveActive().catch(() => {});
      setKitchenMsg((prev) => prev ?? (encoladas
        ? `${pendingCount} platillo${pendingCount !== 1 ? 's' : ''} en espera. Se manda${pendingCount !== 1 ? 'n' : ''} a cocina en cuanto se pueda.`
        : `${pendingCount} platillo${pendingCount !== 1 ? 's' : ''} a preparación`));
      setTimeout(() => setKitchenMsg(null), encoladas ? 7000 : 4000);
    } catch (err) {
      setKitchenMsg(err.response?.data?.error ?? 'No se pudo enviar a cocina');
    } finally {
      setFiring(false);
    }
  };

  const handleSaveTab = async () => {
    if (!tabName.trim() && !activeTabId) return;
    try {
      if (activeTabId) {
        await saveActive();
        setKitchenMsg('Cuenta guardada');
        setTimeout(() => setKitchenMsg(null), 2000);
      } else {
        const tab = await createTab(tabName, { items, customer, discount, note });
        // La cuenta nueva se queda abierta en el carrito: el flujo de mesero es
        // guardar y seguir agregando, no guardar y vaciar.
        useTabsStore.getState().openInCart(tab);
      }
      setSaveTabOpen(false);
      setTabName('');
    } catch (err) {
      setKitchenMsg(err.response?.data?.error ?? 'No se pudo guardar la cuenta en la caja');
      setTimeout(() => setKitchenMsg(null), 5000);
    }
  };

  const applyDiscount = () => {
    const v = Number(discValue);
    if (!v || v <= 0) { setDiscOpen(false); return; }
    const amount = discType === 'pct'
      ? Math.min((subtotal * v) / 100, subtotal)
      : Math.min(v, subtotal);
    setDiscount(amount);
    setDiscOpen(false);
    setDiscValue('');
  };

  const clearDiscount = () => {
    setDiscount(0);
    setDiscOpen(false);
    setDiscValue('');
  };

  // Un renglón de la cuenta, en dos líneas de altura fija: el nombre y el
  // importe arriba; abajo cantidad × precio, modificadores, tiempo, comensal y
  // "en cocina" como etiquetas chicas, con el stepper a la derecha. Medía 117
  // px —dos botones de 44 y chips sueltos— y en un carrito de 800 px de alto
  // cabían dos. Ahora cabe en 64 y se leen ocho.
  const renderItem = (item, { conComensal = true } = {}) => {
    // Con modificadores el precio de lista a comparar es el de la línea
    // (base + extras), no el del producto pelón.
    const listPrice   = (item.base_price ?? item.product.price) +
                        (item.modifiers ?? []).reduce((a, m) => a + (m.price_delta ?? 0), 0);
    const discounted  = item.unit_price < listPrice;
    const overStock   = item.product.stock != null && item.quantity > item.product.stock;
    const curso       = Number.isInteger(item.course) ? item.course : 1;

    return (
      <div key={item.lineId} className={`${styles.item} ${overStock ? styles.itemOver : ''}`}>
        <div className={styles.itemTop} onClick={() => setEditItem(item)} role="button" tabIndex={0}
             onKeyDown={(e) => e.key === 'Enter' && setEditItem(item)}>
          <p className={styles.itemName}>{item.product.name}</p>
          <span className={styles.itemTotal}>{fmt(item.total)}</span>
        </div>

        <div className={styles.itemBottom}>
          <div className={styles.chips}>
            <span className={`${styles.itemPrice} ${discounted ? styles.itemPriceDisc : ''}`}>
              {item.quantity > 1 ? `${item.quantity} × ` : ''}{fmtCorto(item.unit_price)}
              {discounted && <span className={styles.itemOrigPrice}>{fmtCorto(listPrice)}</span>}
            </span>
            {item.modifiers?.length > 0 && (
              <span className={styles.itemMods} onClick={() => setEditItem(item)}>
                {item.modifiers.map((m) => m.option_name).join(' · ')}
              </span>
            )}
            {item.note && (
              <span className={styles.itemNota} onClick={() => setEditItem(item)}>✎ {item.note}</span>
            )}
            {/* El tiempo cicla al tocarlo: son tres, y un selector para tres
                opciones es más lento que un toque. */}
            {usaCocina && (
              <button
                className={`${styles.courseChip} ${styles[`course${curso}`] ?? ''}`}
                aria-label={`Tiempo: ${courseName(curso)}. Tocar para cambiar`}
                onClick={(e) => { e.stopPropagation(); setCourse(item.lineId, (curso % 3) + 1); }}
              >
                {courseName(curso)}
              </button>
            )}
            {/* A quién es. Un toque abre "¿De quién es?": antes rotaba al
                siguiente comensal y la línea saltaba de bloque bajo el dedo. */}
            {esRestaurante && conComensal && comensalesEnMesa.length > 0 && (
              <button
                className={`${styles.seatChip} ${item.seats?.length ? styles.seatChipOn : ''}`}
                aria-label={`De ${seatsLabel(item.seats, comensales)}. Tocar para cambiar`}
                onClick={(e) => { e.stopPropagation(); setSeatsPara(item); }}
              >
                {item.seats?.length > 0 && <span className={styles.seatChipDot} aria-hidden="true" />}
                {seatsLabel(item.seats, comensales)}
              </button>
            )}
            {item.fired_qty > 0 && (
              <span className={styles.itemFired}>
                ✓ {item.fired_qty >= item.quantity ? 'cocina' : `${item.fired_qty} en cocina`}
              </span>
            )}
            {overStock && (
              <span className={styles.itemStockWarn}><IcoWarning size={12} /> Solo {item.product.stock}</span>
            )}
          </div>

          <div className={styles.qtyControl}>
            <button className={styles.qtyBtn} aria-label="Uno menos" onClick={() => updateQty(item.lineId, item.quantity - 1)}>−</button>
            <span className={`${styles.qty} ${overStock ? styles.qtyOver : ''}`}>{item.quantity}</span>
            <button className={styles.qtyBtn} aria-label="Uno más" onClick={() => updateQty(item.lineId, item.quantity + 1)}>+</button>
          </div>
        </div>
      </div>
    );
  };

  // A quién se le apunta lo que se agregue ahora (panel de tablet). En
  // teléfono lo sustituye la pastilla de la barra inferior, que abre la hoja
  // "¿A quién le apunto?": con cuatro comensales esta fila ya no cabía.
  const barraComensales = esRestaurante && !telefono ? (
    <div className={styles.seats}>
      <span className={styles.seatsLead}>Para</span>
      <button
        className={`${styles.seatBtn} ${!activeSeat ? styles.seatBtnOn : ''}`}
        onClick={() => setActiveSeat(null)}
        aria-pressed={!activeSeat}
      >
        {!activeSeat && <span className={styles.seatTick} aria-hidden="true">✓</span>}
        Mesa
      </button>
      {comensalesEnMesa.map((n) => (
        <button
          key={n}
          className={`${styles.seatBtn} ${activeSeat === n ? styles.seatBtnOn : ''}`}
          onClick={() => (activeSeat === n ? setComensalPara(n) : setActiveSeat(n))}
          aria-pressed={activeSeat === n}
          aria-label={`Apuntarle a ${nombreDe(n, comensales)}`}
        >
          {activeSeat === n && <span className={styles.seatTick} aria-hidden="true">✓</span>}
          {nombreDe(n, comensales)}
        </button>
      ))}
      <button
        className={styles.seatAdd}
        onClick={() => { const n = addComensal(); if (n) setActiveSeat(n); }}
      >
        + comensal
      </button>
    </div>
  ) : null;

  const nPlatillos = items.reduce((n, i) => n + i.quantity, 0);
  const hayMarchar = pendingCount > 0 && tiempoAMarchar !== null;

  // Los comensales que todavía no piden, todos en un renglón. Cada uno tenía
  // su bloque de 95 px "Todavía no pide nada": con una mesa de cuatro recién
  // sentada la lista era puros huecos.
  const sinPedir = bloques ? bloques.filter((b) => Number.isInteger(b.seat) && !b.items.length) : [];
  const conPedido = bloques ? bloques.filter((b) => !(Number.isInteger(b.seat) && !b.items.length)) : null;

  const lista = (
    <div className={styles.items}>
      {items.length === 0 && !sinPedir.length ? (
        <div className={styles.empty}>Agrega productos del catálogo</div>
      ) : conPedido ? (
        /* La cuenta en bloques, igual que sale en el ticket: un lugar al que
           se vuelve por comensal, en vez de una lista cronológica con chips. */
        <>
          {conPedido.map((b) => {
            const suma = b.items.reduce((a, i) => a + Number(i.total ?? 0), 0);
            const esComensal = Number.isInteger(b.seat);
            const activo = esComensal ? activeSeat === b.seat : (b.mesa && !activeSeat);
            return (
              <section key={b.clave} className={`${styles.grupo} ${activo ? styles.grupoOn : ''}`}>
                <header className={styles.grupoHead}>
                  {b.seats ? (
                    <span className={styles.grupoNombre}>
                      Comparten {b.seats.map((n) => nombreDe(n, comensales)).join(' y ')}
                    </span>
                  ) : (
                    <button
                      className={styles.grupoBtn}
                      onClick={() => setActiveSeat(esComensal ? b.seat : null)}
                      aria-pressed={activo}
                    >
                      <span className={styles.grupoNombre}>{esComensal ? nombreDe(b.seat, comensales) : 'Para la mesa'}</span>
                      {activo && <span className={styles.grupoAqui}>apuntando aquí</span>}
                    </button>
                  )}
                  {suma > 0 && <span className={styles.grupoTotal}>{fmt(suma)}</span>}
                  {esComensal && (
                    <button
                      className={styles.grupoEdit}
                      onClick={() => setComensalPara(b.seat)}
                      aria-label={`Editar ${nombreDe(b.seat, comensales)}`}
                    >
                      <IcoEdit size={14} />
                    </button>
                  )}
                </header>
                {/* Dentro de un bloque el encabezado ya dice de quién es: el
                    chip por línea solo se muestra en lo compartido. */}
                {b.items.map((i) => renderItem(i, { conComensal: !esComensal }))}
              </section>
            );
          })}
          {sinPedir.length > 0 && (
            <div className={styles.sinPedir}>
              <span className={styles.sinPedirLead}>Sin pedir</span>
              {sinPedir.map((b) => (
                <button
                  key={b.clave}
                  className={`${styles.sinPedirChip} ${activeSeat === b.seat ? styles.sinPedirChipOn : ''}`}
                  onClick={() => setActiveSeat(b.seat)}
                  aria-pressed={activeSeat === b.seat}
                >
                  {nombreDe(b.seat, comensales)}
                </button>
              ))}
              <span className={styles.sinPedirHint}>toca uno para apuntarle</span>
            </div>
          )}
        </>
      ) : (
        items.map((i) => renderItem(i))
      )}
      {/* En comandera el cobro no es de este aparato. Un renglón al final de
          la lista, no una banda fija en la zona del pulgar. */}
      {!permisos.cobrar && items.length > 0 && (
        <p className={styles.cobraCaja}>El cobro lo hace caja. Deja la cuenta abierta y ahí la cierran.</p>
      )}
    </div>
  );

  // Deshacer el último cambio de comensal. Cinco segundos: lo que tarda el
  // mesero en ver que se equivocó, y no tanto como para estorbar.
  const deshacerUI = deshacer && (
    <div className={styles.deshacer} role="status">
      <span>{deshacer.texto}</span>
      <button
        className={styles.deshacerBtn}
        onClick={() => { setItemSeats(deshacer.lineId, deshacer.seats); setDeshacer(null); }}
      >
        Deshacer
      </button>
    </div>
  );

  // Guardar como cuenta (mostrador sin cuenta abierta) o el estado del
  // guardado automático: un botón de verdad solo cuando hay algo esperando.
  const estadoCuenta = items.length > 0 && (
    saveTabOpen && !activeTabId ? (
      <div className={styles.tabSaveRow}>
        <input
          className={styles.tabNameInput}
          placeholder="Nombre de la cuenta (Mesa 3, Juan…)"
          value={tabName}
          onChange={(e) => setTabName(e.target.value)}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTab(); if (e.key === 'Escape') setSaveTabOpen(false); }}
        />
        <button className="btn btn--primary" style={{ fontSize: 13, padding: '0 12px', minHeight: 40, flexShrink: 0 }} onClick={handleSaveTab}>
          Guardar
        </button>
        <button className="btn btn--ghost" style={{ padding: '0 10px', minHeight: 40, flexShrink: 0 }} onClick={() => setSaveTabOpen(false)} aria-label="Cancelar">
          <IcoClose size={13} />
        </button>
      </div>
    ) : activeTabId ? (
      pendienteDeSubir ? (
        <button className={styles.updateTabBtn} onClick={handleSaveTab} disabled={saving}>
          <IcoArrowUp size={13} /> {saving ? 'Guardando…' : 'Reintentar el guardado'}
        </button>
      ) : null
    ) : (
      <button className={styles.saveTabBtn} onClick={() => { setSaveTabOpen(true); setTabName(''); }}>
        <IcoTabs size={14} /> Guardar como cuenta…
      </button>
    )
  );

  // Nota y descuento en una sola fila; abiertos, ocupan la fila entera.
  const notaUI = (noteOpen || note) && (
    <div className={styles.noteInputRow}>
      <input
        className={styles.noteInput}
        placeholder="Nota de la venta…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        autoFocus={noteOpen}
        // Al salir del campo la nota queda escrita y en tablet vuelve a ser
        // un chip; Enter y Escape hacen lo mismo.
        onBlur={() => setNoteOpen(false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur(); }}
        maxLength={120}
      />
      {/* Sin el preventDefault, el toque saca el foco del campo, el renglón
          se cierra y el clic ya no encuentra el botón. */}
      <button className={styles.noteClear} onMouseDown={(e) => e.preventDefault()} onClick={() => { setNote(''); setNoteOpen(false); }} aria-label="Quitar nota"><IcoClose size={14} /></button>
    </div>
  );

  const descuentoUI = permisos.cobrar && items.length > 0 && (
    discount > 0 && !discOpen ? (
      <div className={styles.discountApplied}>
        <span className={styles.discountTag}>Descuento −{fmt(discount)}</span>
        <button className={styles.discountClear} onClick={clearDiscount} aria-label="Quitar descuento">×</button>
      </div>
    ) : discOpen ? (
      <div className={styles.discountInput}>
        <div className={styles.discPresets}>
          {[10, 15, 20, 25].map((pct) => (
            <button
              key={pct}
              className={styles.discPresetBtn}
              onClick={() => { setDiscount(Math.round(subtotal * pct / 100 * 100) / 100); setDiscOpen(false); }}
            >{pct}%</button>
          ))}
        </div>
        <div className={styles.discountInputRow}>
          <div className={styles.discTypeToggle}>
            <button className={`${styles.typeBtn} ${discType === 'pct' ? styles.typeBtnActive : ''}`} onClick={() => setDiscType('pct')}>%</button>
            <button className={`${styles.typeBtn} ${discType === 'fixed' ? styles.typeBtnActive : ''}`} onClick={() => setDiscType('fixed')}>$</button>
          </div>
          <input
            className={styles.discValueInput}
            type="number"
            inputMode="decimal"
            min="0"
            max={discType === 'pct' ? 100 : undefined}
            placeholder={discType === 'pct' ? '10' : subtotal.toFixed(2)}
            value={discValue}
            autoFocus
            onChange={(e) => setDiscValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyDiscount()}
          />
          <button className={styles.discApply} onClick={applyDiscount} aria-label="Aplicar descuento"><IcoCheck size={14} /></button>
          <button className={styles.discCancel} onClick={() => setDiscOpen(false)} aria-label="Cancelar">×</button>
        </div>
      </div>
    ) : null
  );

  // Lo que ajusta la venta, en un renglón junto al total: cliente, nota,
  // descuento y lector. Eran dos franjas, una arriba de la lista y otra abajo,
  // y con la de "Guardar como cuenta…" en una tablet de 600 px de alto la
  // lista de lo pedido se quedaba en un renglón. La terminal propia no lleva
  // chip: no hay nada que tocarle desde aquí.
  const ajustes = [
    (permisos.cobrar || customer) && (
      <button key="cliente" className={styles.estadoChip} onClick={() => setPickerOpen(true)}>
        <IcoUser size={13} /> <span className={styles.chipTexto}>{customer ? customer.name : 'Cliente'}</span>
      </button>
    ),
    // La nota escrita es un chip con su texto; el campo solo se abre para
    // escribirla. Abierto todo el tiempo era un renglón más bajo la lista.
    items.length > 0 && !noteOpen && (
      note ? (
        <button key="nota" className={`${styles.estadoChip} ${styles.chipEncoge}`} onClick={() => setNoteOpen(true)} aria-label={`Nota: ${note}. Tocar para cambiar`}>
          <IcoEdit size={13} /> <span className={styles.chipTexto}>{note}</span>
        </button>
      ) : (
        <button key="nota" className={`${styles.estadoChip} ${styles.chipAgregar}`} onClick={() => setNoteOpen(true)}>+ Nota</button>
      )
    ),
    permisos.cobrar && items.length > 0 && !discOpen && !(discount > 0) && (
      <button key="desc" className={`${styles.estadoChip} ${styles.chipAgregar}`} onClick={() => setDiscOpen(true)}>+ Descuento</button>
    ),
    permisos.cobrar && terminal?.terminal_provider !== 'external' && (
      <button key="lector" className={styles.estadoChip} onClick={() => navigate('/reader-picker')}>
        <span className={`${styles.terminalDot} ${terminalStatus === 'connected' ? styles.terminalDotOn : ''}`} />
        {terminalStatus === 'connected'
          ? (terminalReader?.label ?? terminalReader?.serialNumber ?? 'Lector')
          : 'Sin lector'}
      </button>
    ),
  ].filter(Boolean);
  const ajustesUI = !telefono && ajustes.length > 0 && <div className={styles.ajustes}>{ajustes}</div>;

  const totalesUI = (
    <div className={styles.totals}>
      {discount > 0 && (
        <>
          <div className={styles.totalRow}><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
          <div className={`${styles.totalRow} ${styles.discountRow}`}><span>Descuento</span><span>−{fmt(discount)}</span></div>
        </>
      )}
      <div className={`${styles.totalRow} ${styles.grandTotal}`}>
        <span>Total</span><strong>{fmt(total)}</strong>
      </div>
    </div>
  );

  const marcharBtn = hayMarchar && (
    <button
      className={`btn btn--full ${styles.fireBtn}`}
      disabled={firing}
      onClick={handleFireKitchen}
    >
      {/* Verbo y detalle por separado: en pantalla baja los dos botones van
          lado a lado y cada uno se parte en dos renglones. */}
      {firing
        ? <><span className="spinner" /> Enviando…</>
        : <><span>Marchar</span> <span className={styles.accionDetalle}>{courseName(tiempoAMarchar).toLowerCase()} · {pendingCount}</span></>}
    </button>
  );

  // CTA — con cobro centralizado el operador levanta la orden y la deja
  // lista; cerrarla es de caja.
  const cobrarBtn = permisos.cobrar && (
    <button
      className={`btn btn--primary btn--full ${styles.cobrarBtn}`}
      disabled={items.length === 0}
      onClick={() => navigate('/checkout')}
    >
      <span>Cobrar</span>
      {items.length > 0 && <span className={styles.accionDetalle}>{fmt(total)}</span>}
    </button>
  );

  // Lo secundario, en un menú: eran seis iconos de 32 px en la cabecera.
  const menuItems = [
    esRestaurante && !activeTab?.tableId && { label: 'Asignar mesa', hint: activeTabId ? 'Amarrar esta cuenta a una mesa del plano' : 'La cuenta se abre en esa mesa', icon: <IcoTabs size={18} />, onClick: () => setAsignarOpen(true) },
    { label: customer ? `Cliente: ${customer.name}` : 'Agregar cliente', hint: customer ? 'Cambiar o quitar' : 'Para facturar o llevar historial', icon: <IcoUser size={18} />, onClick: () => setPickerOpen(true) },
    telefono && { label: note ? 'Nota de la venta' : 'Agregar nota', hint: note || 'Va en el ticket y en la comanda', icon: <IcoEdit size={18} />, onClick: () => setNoteOpen(true) },
    telefono && permisos.cobrar && items.length > 0 && { label: 'Descuento', hint: discount > 0 ? `Aplicado −${fmt(discount)}` : 'Porcentaje o monto', icon: <IcoCash size={18} />, onClick: () => setDiscOpen(true) },
    !activeTabId && items.length > 0 && { label: 'Guardar como cuenta…', hint: 'Para cobrarla después', icon: <IcoTabs size={18} />, onClick: () => { setSaveTabOpen(true); setTabName(''); } },
    permisos.moverCaja && { label: 'Movimiento de caja', hint: 'Entrada o salida de efectivo', icon: <IcoCash size={18} />, onClick: () => setMovementOpen(true) },
    !permisos.comandera && { label: 'Historial de ventas', icon: <IcoReceipt size={18} />, onClick: () => navigate('/sales-history') },
    activeTab && { label: 'Desvincular cuenta', hint: 'No la elimina; queda abierta en la caja', icon: <IcoClose size={18} />, onClick: () => { clearActiveTab(); clearCart(); if (telefono) setHojaAbierta(false); } },
    items.length > 0 && { label: 'Vaciar carrito', hint: activeTab ? 'Quita todo de esta cuenta' : undefined, icon: <IcoTrash size={18} />, danger: true, onClick: async () => { if (await pedir({ titulo: '¿Vaciar el carrito?', texto: activeTab ? 'Se quitan todos los platillos de esta cuenta y se desvincula.' : 'Se quitan todos los platillos.', confirmar: 'Vaciar', peligro: true })) { clearCart(); clearActiveTab(); } } },
  ];

  const hojas = (
    <>
      {pickerOpen && <CustomerPickerModal onClose={() => setPickerOpen(false)} />}
      {editItem && (
        <ItemEditSheet
          item={editItem}
          comensales={comensales}
          puedeCobrar={permisos.cobrar}
          // Solo hay a quién asignarle si hay gente sentada; en un mostrador
          // la pregunta no significa nada.
          onCompartir={esRestaurante && comensalesEnMesa.length > 0
            ? (item) => { setEditItem(null); setSeatsPara(item); }
            : null}
          onClose={() => setEditItem(null)}
        />
      )}
      {movementOpen && <CashMovementModal onClose={() => setMovementOpen(false)} />}

      <SeatsSheet
        open={!!seatsPara}
        linea={seatsPara}
        seats={comensalesEnMesa}
        comensales={comensales}
        onClose={() => setSeatsPara(null)}
        onAgregar={() => addComensal()}
        onGuardar={(sel) => moverA(seatsPara, sel, sel.length > 1 ? 'Ahora lo comparten' : (sel.length ? `Ahora es de ${nombreDe(sel[0], comensales)}` : 'Ahora es de la mesa'))}
      />

      <ApuntarASheet
        open={apuntarOpen}
        seats={comensalesEnMesa}
        comensales={comensales}
        activeSeat={activeSeat}
        onElegir={setActiveSeat}
        onAgregar={() => addComensal()}
        onEditar={(n) => { setApuntarOpen(false); setComensalPara(n); }}
        onClose={() => setApuntarOpen(false)}
      />

      <ComensalSheet
        open={Number.isInteger(comensalPara)}
        seat={comensalPara}
        comensales={comensales}
        consumo={items.filter((i) => (i.seats ?? []).includes(comensalPara)).length}
        onClose={() => setComensalPara(null)}
        onRename={renameComensal}
        onRemove={removeComensal}
      />

      <MenuSheet open={menuOpen} title={activeTab?.name ?? 'Cuenta'} items={menuItems} onClose={() => setMenuOpen(false)} />
      {Confirmacion}

      <AssignTableSheet
        open={asignarOpen}
        tabId={activeTabId}
        onClose={() => setAsignarOpen(false)}
        onAssign={async (mesa) => {
          // Con cuenta abierta se le amarra la mesa; sin ella, lo que hay en el
          // carrito se convierte en la cuenta de esa mesa. Es el mismo criterio
          // que usa el plano al sentar, para que las dos puertas hagan lo mismo.
          const tab = activeTabId
            ? await useTabsStore.getState().bindTable(activeTabId, mesa._id, mesa.name)
            : await createTab(mesa.name, { items, customer, discount, note, tableId: mesa._id, tableName: mesa.name });
          useTabsStore.getState().openInCart(tab);
        }}
      />
    </>
  );

  // ── Teléfono ─────────────────────────────────────────────────────────────
  // Una barra de 64 px con lo único que hace falta mientras se captura: a
  // quién se le apunta, cuánto va y Marchar. La cuenta completa abre a
  // pantalla entera con su propia cabecera. Antes era una hoja al 88 % con
  // nueve bandas fijas que dejaban dos renglones para los platillos.
  if (telefono) {
    return (
      <>
        <div className={styles.barra}>
          {esRestaurante && (
            <button className={styles.barraComensal} onClick={() => setApuntarOpen(true)} aria-label="A quién le apunto">
              <IcoUser size={14} />
              <strong>{activeSeat ? nombreDe(activeSeat, comensales) : 'Mesa'}</strong>
              <span className={styles.barraCaret} aria-hidden="true">▾</span>
            </button>
          )}
          <button className={styles.barraResumen} onClick={() => setHojaAbierta(true)} aria-expanded={hojaAbierta}>
            {items.length === 0 ? (
              <span className={styles.barraVacia}>Cuenta vacía</span>
            ) : (
              <>
                <strong>{fmt(total)}</strong>
                <small>{nPlatillos} platillo{nPlatillos !== 1 ? 's' : ''}</small>
              </>
            )}
          </button>
          {permisos.cobrar && items.length > 0 ? (
            <button className={styles.barraPrimaria} onClick={() => navigate('/checkout')}>Cobrar</button>
          ) : hayMarchar ? (
            <button className={styles.barraPrimaria} disabled={firing} onClick={handleFireKitchen}>
              {firing ? 'Enviando…' : `Marchar · ${pendingCount}`}
            </button>
          ) : (
            <button className={styles.barraSecundaria} onClick={() => setHojaAbierta(true)}>Cuenta</button>
          )}
        </div>

        {kitchenMsg && <p className={styles.toast} role="status">{kitchenMsg}</p>}

        {hojaAbierta && (
          <div className={styles.hoja} role="dialog" aria-modal="true" aria-label="Cuenta">
            <div className={styles.hojaHead}>
              <button className={styles.hojaBtn} onClick={() => setHojaAbierta(false)} aria-label="Volver al catálogo">←</button>
              <div className={styles.hojaTitulo}>
                <strong>{activeTab?.name ?? 'Cuenta'}</strong>
                <small>
                  {[
                    comensalesEnMesa.length ? `${comensalesEnMesa.length} comensal${comensalesEnMesa.length !== 1 ? 'es' : ''}` : null,
                    customer?.name ?? null,
                    saving ? 'Guardando…' : (activeTabId && !pendienteDeSubir ? 'Guardado' : null),
                  ].filter(Boolean).join(' · ')}
                </small>
              </div>
              <button className={styles.hojaBtn} onClick={() => setMenuOpen(true)} aria-label="Más opciones">⋯</button>
            </div>

            {estadoCuenta && <div className={styles.hojaEstado}>{estadoCuenta}</div>}
            {(noteOpen || note) && <div className={styles.hojaEstado}>{notaUI}</div>}
            {(discOpen || discount > 0) && <div className={styles.hojaEstado}>{descuentoUI}</div>}

            {lista}
            {deshacerUI}

            <div className={styles.hojaPie}>
              <div className={styles.hojaTotal}>
                <small>Total{discount > 0 ? ` · desc. −${fmt(discount)}` : ''}</small>
                <strong>{fmt(total)}</strong>
              </div>
              {permisos.cobrar ? (
                <button className={styles.barraPrimaria} disabled={items.length === 0} onClick={() => navigate('/checkout')}>Cobrar</button>
              ) : hayMarchar ? (
                <button className={styles.barraPrimaria} disabled={firing} onClick={handleFireKitchen}>
                  {firing ? 'Enviando…' : `Marchar ${courseName(tiempoAMarchar).toLowerCase()} · ${pendingCount}`}
                </button>
              ) : (
                <button className={styles.barraSecundaria} onClick={() => setHojaAbierta(false)}>Seguir pidiendo</button>
              )}
            </div>
          </div>
        )}

        {hojas}
      </>
    );
  }

  // ── Tablet ───────────────────────────────────────────────────────────────
  return (
    <aside className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {activeTab ? (
            <div className={styles.activeTabBadge}>
              <span className={styles.activeTabDot} />
              <span className={styles.activeTabName}>{activeTab.name}</span>
            </div>
          ) : (
            <span className={styles.title}>Carrito</span>
          )}
          {esRestaurante && !activeTab?.tableId && (
            <button className={styles.headerChip} onClick={() => setAsignarOpen(true)}>Asignar mesa</button>
          )}
          {/* En mostrador, guardar la venta para cobrarla después. Era una
              franja propia bajo la cabecera; en restaurante lo hace "Asignar
              mesa" y "Guardar como cuenta…" sigue en el menú. */}
          {!esRestaurante && !activeTabId && items.length > 0 && !saveTabOpen && (
            <button className={styles.headerChip} onClick={() => { setSaveTabOpen(true); setTabName(''); }}>Guardar cuenta</button>
          )}
          {activeTabId && (
            <span className={styles.guardado}>{saving ? 'Guardando…' : (pendienteDeSubir ? '' : 'Guardado')}</span>
          )}
        </div>
        <button className={styles.headerMenu} onClick={() => setMenuOpen(true)} aria-label="Más opciones">⋯</button>
      </div>

      {/* Solo cuando hay algo que hacer: escribir el nombre de la cuenta o
          reintentar un guardado que falló. */}
      {estadoCuenta && (saveTabOpen || activeTabId) && <div className={styles.tabBar}>{estadoCuenta}</div>}

      {barraComensales}
      {lista}
      {deshacerUI}

      <div className={styles.pie}>
        {ajustesUI}
        {noteOpen && <div className={styles.pieFila}>{notaUI}</div>}
        {(discOpen || discount > 0) && <div className={styles.pieFila}>{descuentoUI}</div>}
        {totalesUI}
        {kitchenMsg && <p className={styles.kitchenMsg}>{kitchenMsg}</p>}
        <div className={styles.acciones}>
          {marcharBtn}
          {cobrarBtn || (
            <p className={styles.kitchenMsg} style={{ marginBottom: 0, textAlign: 'center' }}>
              El cobro lo hace caja. Deja la cuenta abierta y ahí la cierran.
            </p>
          )}
        </div>
      </div>

      {hojas}
    </aside>
  );
}
